import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaCopy, FaEnvelope, FaPaperPlane } from 'react-icons/fa';

/**
 * Shows the fresh sign-in link after an invite / resend-invite so the admin can
 * copy it and send it directly (WhatsApp, SMS…) — essential when SendGrid isn't
 * configured, and handy anyway when the email goes to spam.
 *
 * Props: open, onClose, company, email, link, emailSent
 */
function InviteLinkModal({ open, onClose, company, email, link, emailSent }) {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const fallbackCopy = () => {
    try {
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (e) {
      /* ignore */
    }
  };

  const handleCopy = () => {
    const done = () => {
      setCopied(true);
      toast.success('Sign-in link copied');
      setTimeout(() => setCopied(false), 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(() => { fallbackCopy(); done(); });
    } else {
      fallbackCopy();
      done();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-full bg-green/10 text-green flex items-center justify-center">
              <FaPaperPlane size={14} />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-navy">Invite link ready</h3>
              <p className="text-xs text-gray-500">
                {company ? `${company} · ${email}` : email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Close"
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div
            className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm ${
              emailSent ? 'bg-green/10 text-green-800' : 'bg-amber-50 text-amber-800'
            }`}
          >
            <FaEnvelope className="mt-0.5 shrink-0" size={14} />
            <span>
              {emailSent
                ? `Invitation emailed to ${email}.`
                : 'Email could not be sent (SendGrid not configured) — copy the link below and send it to them directly.'}
            </span>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-2">
              Fresh sign-in link
            </label>
            <div className="flex items-stretch gap-2">
              <input
                readOnly
                value={link || ''}
                onFocus={(e) => e.target.select()}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono text-gray-600 bg-gray-50"
              />
              <button
                onClick={handleCopy}
                disabled={!link}
                className="px-4 py-2 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: copied ? '#0A1929' : '#00A86B' }}
              >
                <FaCopy size={13} /> {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            The link signs them in once and then expires — they'll be asked to set a password.
            Just click <strong>Resend invite</strong> again any time you need a new one.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default InviteLinkModal;
